import type { TeacherLessonRecord } from "../auth/api-client";

export type LessonLibrarySortKey =
  | "title"
  | "updated"
  | "studentsFinished"
  | "studentsAssigned"
  | "usedInTemplates"
  | "activeCohorts";

export const LESSON_LIBRARY_SORT_OPTIONS: { readonly key: LessonLibrarySortKey; readonly label: string }[] = [
  { key: "title", label: "Title (A–Z)" },
  { key: "updated", label: "Recently modified" },
  { key: "studentsFinished", label: "Most completions" },
  { key: "studentsAssigned", label: "Most assigned" },
  { key: "usedInTemplates", label: "Most templates" },
  { key: "activeCohorts", label: "Most active cohorts" },
];

const SORT_STORAGE_KEY = "ccs-teacher-lessons-sort";

export function loadLessonLibrarySortKey(): LessonLibrarySortKey {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (LESSON_LIBRARY_SORT_OPTIONS.some((o) => o.key === raw)) return raw as LessonLibrarySortKey;
  } catch {
    /* ignore */
  }
  return "title";
}

export function saveLessonLibrarySortKey(key: LessonLibrarySortKey): void {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, key);
  } catch {
    /* ignore */
  }
}

export function sortLessonLibrary(
  lessons: readonly TeacherLessonRecord[],
  key: LessonLibrarySortKey
): TeacherLessonRecord[] {
  const copy = [...lessons];
  copy.sort((a, b) => {
    switch (key) {
      case "title":
        return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
      case "updated":
        return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
      case "studentsFinished":
        return (b.studentsFinished ?? 0) - (a.studentsFinished ?? 0) || a.title.localeCompare(b.title);
      case "studentsAssigned":
        return (b.studentsAssigned ?? 0) - (a.studentsAssigned ?? 0) || a.title.localeCompare(b.title);
      case "usedInTemplates":
        return (b.usedInTemplates ?? 0) - (a.usedInTemplates ?? 0) || a.title.localeCompare(b.title);
      case "activeCohorts":
        return (b.activeCohorts ?? 0) - (a.activeCohorts ?? 0) || a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });
  return copy;
}
